(() => {
  const previewParameters = ['preview_theme_id', '_fd', 'pb'];
  const policyHandles = new Set(['oterms-of-service', 'privacy-policy', 'warranty-policy']);
  const imageSizes = '(min-width: 1920px) 442px, (min-width: 1200px) calc((100vw - 152px) / 4), calc((100vw - 48px) / 2)';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function orderedUnique(items, excluded) {
    const map = new Map();
    for (const item of items) {
      if (policyHandles.has(item.handle.split('/').pop()) || excluded.has(String(item.id))) continue;
      if (!map.has(String(item.id))) map.set(String(item.id), item);
    }
    return [...map.values()].sort((a, b) => Date.parse(b.published) - Date.parse(a.published) || String(b.id).localeCompare(String(a.id), 'en', { numeric: true }));
  }

  class CommunityMasonry extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.excluded = new Set((this.config.excluded || []).map(String));
      this.active = this.config.active;
      this.grid = this.querySelector('[data-grid]');
      this.panel = this.querySelector('[data-panel]');
      this.tabs = [...this.querySelectorAll('[data-category]')];
      this.status = this.querySelector('[data-status]');
      this.empty = this.querySelector('[data-empty]');
      this.expand = this.querySelector('[data-expand]');
      this.sourceCache = new Map();
      this.partial = new Map();
      this.categoryCache = new Map([[this.active, this.config.seed]]);
      this.complete = new Set();
      this.expanded = false;
      this.failed = false;
      this.loading = false;
      this.sequence = 0;
      this.querySelector('[data-tabs]').setAttribute('role', this.config.channelPage ? 'navigation' : 'tablist');
      this.panel.setAttribute('role', this.config.channelPage ? 'region' : 'tabpanel');
      this.tabs.forEach(tab => {
        if (!this.config.channelPage) {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', this.panel.id);
        }
        tab.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (this.config.channelPage || tab.dataset.category === 'inside') return;
          event.preventDefault();
          this.select(tab.dataset.category);
        }, { signal: this.controller.signal });
        tab.addEventListener('keydown', event => {
          if (event.key === ' ') {
            event.preventDefault();
            if (this.config.channelPage || tab.dataset.category === 'inside') {
              tab.click();
              return;
            }
            this.select(tab.dataset.category);
            return;
          }
          if (this.config.channelPage) return;
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.tabs[next].focus({ preventScroll: true });
          if (this.tabs[next].dataset.category !== 'inside') this.select(this.tabs[next].dataset.category);
        }, { signal: this.controller.signal });
      });
      this.expand.addEventListener('click', () => {
        if (this.loading) return;
        if (this.expanded) {
          this.expanded = false;
          // Scroll before removing tall columns; avoid leaving focus outside the viewport.
          const top = this.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
          this.render();
          this.tabs.find(tab => tab.dataset.category === this.active).focus({ preventScroll: true });
        } else {
          this.load(true);
        }
      }, { signal: this.controller.signal });
      this.grid.addEventListener('load', () => this.scheduleLayout(), { capture: true, signal: this.controller.signal });
      this.grid.addEventListener('error', event => {
        if (event.target.tagName === 'IMG') {
          this.failedImage(event.target);
        }
      }, { capture: true, signal: this.controller.signal });
      // Cached or immediately rejected images may fail before this element is upgraded.
      this.grid.querySelectorAll('img').forEach(image => {
        if (image.complete && image.naturalWidth === 0) this.failedImage(image);
      });
      this.width = 0;
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(entries => {
          if (entries.some(entry => entry.target !== this.grid || Math.abs(entry.contentRect.width - this.width) > .5)) {
            this.width = this.grid.getBoundingClientRect().width;
            this.scheduleLayout();
          }
        });
        this.observer.observe(this.grid);
        this.observeCards();
      }
      window.addEventListener('resize', () => this.scheduleLayout(), { signal: this.controller.signal });
      document.fonts?.ready.then(() => { if (this.isConnected) this.scheduleLayout(); });
      document.fonts?.addEventListener('loadingdone', () => this.scheduleLayout(), { signal: this.controller.signal });
      this.updateTabs();
      this.updateControls();
      this.scheduleLayout();
    }

    failedImage(image) {
      image.parentElement.classList.add('wf-cm-card__media--empty');
      image.hidden = true;
      this.scheduleLayout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.controller = null;
    }

    updateTabs() {
      this.tabs.forEach(tab => {
        const selected = tab.dataset.category === this.active;
        if (this.config.channelPage) {
          tab.tabIndex = 0;
          if (selected) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        } else {
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          tab.removeAttribute('aria-current');
        }
        if (selected) this.panel.setAttribute('aria-labelledby', tab.id);
      });
      const tab = this.tabs.find(t => t.dataset.category === this.active);
      const rail = tab.parentElement;
      if (rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: Math.max(0, tab.offsetLeft - rail.offsetLeft - (rail.clientWidth - tab.offsetWidth) / 2), behavior: reducedMotion() ? 'instant' : 'smooth' });
      }
    }

    select(category) {
      if (category === this.active) return;
      this.sequence++;
      this.request?.abort();
      this.active = category;
      this.expanded = false;
      this.failed = false;
      this.loading = false;
      this.updateTabs();
      this.render();
      if (!this.complete.has(category)) this.load(false);
    }

    sourcesFor(category) {
      return category === 'all' || category === 'inside' ? Object.entries(this.config.sources) : [[category, this.config.sources[category]]];
    }

    filter(items, category) {
      const legacy = category === 'all' || category === 'inside' ? this.config.legacy || [] : [];
      const unique = orderedUnique([...legacy, ...items], this.excluded);
      if (category === 'all') return unique;
      if (category === 'inside') return unique.filter(item => item.brand);
      return unique.filter(item => !item.brand && item.blog === this.config.sources[category].handle);
    }

    async readSource(source, signal) {
      if (!source?.handle || !source.url) throw new Error('Missing blog source');
      if (this.sourceCache.has(source.handle)) return this.sourceCache.get(source.handle);
      const items = [];
      const seenPages = new Set();
      let next = source.url;
      while (next) {
        const url = new URL(next, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid feed origin');
        const current = new URL(window.location.href);
        previewParameters.forEach(key => { if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key)); });
        url.searchParams.set('section_id', 'wf-community-feed');
        if (seenPages.has(url.href)) throw new Error('Repeating feed page');
        seenPages.add(url.href);
        const timeout = AbortSignal.timeout(20000);
        const response = await fetch(url.href, { signal: AbortSignal.any([signal, timeout]), credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Feed ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('script[data-community-feed]');
        if (!script) throw new Error('Feed unavailable');
        const payload = JSON.parse(script.textContent);
        if (payload.blog !== source.handle || !Array.isArray(payload.items)) throw new Error('Invalid feed');
        for (const item of payload.items) {
          if (!item.id || !item.url || !item.title || !Number.isFinite(Date.parse(item.published))) throw new Error('Invalid article');
          const target = new URL(item.url, window.location.origin);
          if (target.origin !== window.location.origin || !target.pathname.includes('/blogs/')) throw new Error('Invalid article link');
          if (item.blog !== source.handle) throw new Error('Wrong source article');
        }
        items.push(...payload.items);
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        this.partial.set(source.handle, [...items]);
        next = payload.next;
      }
      this.sourceCache.set(source.handle, items);
      return items;
    }

    async load(expand) {
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      const sequence = ++this.sequence;
      const category = this.active;
      this.loading = true;
      this.failed = false;
      this.updateControls();
      try {
        let items = [];
        for (const [, source] of this.sourcesFor(category)) {
          items.push(...await this.readSource(source, request.signal));
        }
        if (request.signal.aborted || sequence !== this.sequence || category !== this.active) return;
        items = this.filter(items, category);
        this.categoryCache.set(category, items);
        this.complete.add(category);
        this.expanded = expand && items.length > 4;
      } catch (error) {
        if (request.signal.aborted || sequence !== this.sequence || category !== this.active) return;
        const available = this.sourcesFor(category).flatMap(([, source]) => this.sourceCache.get(source?.handle) || this.partial.get(source?.handle) || []);
        const prior = this.categoryCache.get(category) || [];
        this.categoryCache.set(category, this.filter([...available, ...prior], category));
        this.failed = true;
        this.expanded = false;
      } finally {
        if (sequence === this.sequence && category === this.active) {
          this.loading = false;
          this.render();
        }
      }
    }

    fallback(item) {
      if (item.brand) return this.config.fallbacks.inside;
      const source = Object.entries(this.config.sources).find(([, value]) => value.handle === item.blog);
      return this.config.fallbacks[source?.[0]];
    }

    categoryFor(item) {
      if (item.brand) return 'inside';
      return Object.entries(this.config.sources).find(([, source]) => source.handle === item.blog)?.[0];
    }

    card(item) {
      const article = document.createElement('article');
      article.className = 'wf-cm-card';
      article.dataset.articleId = item.id;
      const link = document.createElement('a');
      link.className = 'wf-cm-card__link';
      link.href = item.url;
      link.dataset.cta = 'community_article';
      link.dataset.ctaLocation = 'community_masonry';
      link.dataset.articleHandle = item.handle;
      const media = document.createElement('div');
      media.className = 'wf-cm-card__media';
      const image = item.image || this.fallback(item);
      if (image) {
        const img = document.createElement('img');
        img.width = image.width;
        img.height = image.height;
        img.alt = item.title;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.sizes = imageSizes;
        if (image.srcset) img.srcset = image.srcset;
        img.src = image.src;
        media.append(img);
      } else media.classList.add('wf-cm-card__media--empty');
      if (this.active === 'all') {
        const category = this.categoryFor(item);
        const badge = this.querySelector(`[data-category-badge="${category}"]`);
        if (badge) media.append(badge.content.cloneNode(true));
      }
      const title = document.createElement('h3');
      title.className = 'wf-cm-card__title';
      title.textContent = item.title;
      const time = document.createElement('time');
      time.dateTime = item.published;
      time.textContent = item.date;
      link.append(media, title, time);
      article.append(link);
      return article;
    }

    render() {
      const items = this.categoryCache.get(this.active) || [];
      this.observer?.disconnect();
      this.grid.replaceChildren(...(this.expanded ? items : items.slice(0, 4)).map(item => this.card(item)));
      this.observer?.observe(this.grid);
      this.observeCards();
      this.empty.hidden = items.length > 0 || this.loading || this.failed;
      this.updateControls();
      this.scheduleLayout();
    }

    updateControls() {
      const labels = this.config.labels;
      const items = this.categoryCache.get(this.active) || [];
      this.panel.setAttribute('aria-busy', String(this.loading));
      this.expand.disabled = this.loading;
      const total = this.complete.has(this.active) ? items.length : this.active === this.config.active ? this.config.initialTotal : null;
      this.expand.hidden = !this.loading && !this.failed && total !== null && total !== undefined && total <= 4;
      this.expand.textContent = this.loading ? labels.loading : this.failed ? labels.retry : this.expanded ? labels.less : labels.more;
      this.expand.setAttribute('aria-expanded', String(this.expanded));
      this.expand.setAttribute('aria-controls', this.panel.id);
      this.expand.dataset.cta = this.failed ? 'community_retry' : this.expanded ? 'community_collapse' : 'community_expand';
      this.status.textContent = this.loading ? labels.loading : this.failed ? labels.error : this.expanded ? labels.complete.replace('[count]', items.length) : items.length ? labels.count.replace('[count]', Math.min(4, items.length)) : '';
    }

    observeCards() { this.grid.querySelectorAll('.wf-cm-card').forEach(card => this.observer?.observe(card)); }

    scheduleLayout() {
      if (!this.isConnected) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      const width = this.grid.getBoundingClientRect().width;
      if (!width) return;
      const columns = window.innerWidth >= 1200 ? 4 : 2;
      const style = getComputedStyle(this);
      const gap = parseFloat(style.getPropertyValue(window.innerWidth < 750 ? '--cm-gap-mobile' : '--cm-gap')) || 16;
      const cardWidth = (width - gap * (columns - 1)) / columns;
      const cards = [...this.grid.children];
      cards.forEach(card => { card.style.width = `${cardWidth}px`; });
      const heights = Array(columns).fill(0);
      cards.forEach(card => {
        const column = heights.indexOf(Math.min(...heights));
        const y = heights[column];
        const height = card.getBoundingClientRect().height;
        card.style.transform = `translate(${column * (cardWidth + gap)}px, ${y}px)`;
        heights[column] = y + height + gap;
      });
      this.grid.dataset.masonry = '';
      this.grid.style.height = `${Math.max(0, ...heights) - (cards.length ? gap : 0)}px`;
    }
  }

  class CommunityCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.querySelector('[data-controls]')?.removeAttribute('hidden');
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => this.show(this.index + Number(button.dataset.direction)), { signal: this.controller.signal }));
      this.querySelectorAll('[data-slide-index]').forEach(button => button.addEventListener('click', () => this.show(Number(button.dataset.slideIndex)), { signal: this.controller.signal }));
      this.addEventListener('keydown', event => {
        if (event.target !== this || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.controller.signal });
      this.addEventListener('touchstart', event => {
        const touch = event.touches[0];
        this.touch = { x: touch.clientX, y: touch.clientY };
      }, { passive: true, signal: this.controller.signal });
      this.addEventListener('touchend', event => {
        if (!this.touch) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touch.x;
        const dy = touch.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true, signal: this.controller.signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.indexOf(event.target.closest('[data-slide]'));
        if (index >= 0) this.show(index);
      }, { signal: this.controller.signal });
      this.show(0, false);
    }

    disconnectedCallback() { this.controller?.abort(); this.controller = null; }

    show(index, announce = true) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const selected = i === this.index;
        slide.hidden = !selected;
        slide.inert = !selected;
        slide.setAttribute('aria-hidden', String(!selected));
      });
      this.querySelectorAll('[data-slide-index]').forEach((button, i) => {
        if (i === this.index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (announce) this.querySelector('[data-carousel-status]').textContent = this.slides[this.index].getAttribute('aria-label');
    }
  }

  if (!customElements.get('wf-community-masonry')) customElements.define('wf-community-masonry', CommunityMasonry);
  if (!customElements.get('wf-community-carousel')) customElements.define('wf-community-carousel', CommunityCarousel);
})();

